// src/context/CartContext.jsx
import { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext();

export const useCart = () => useContext(CartContext);

// Helper function to parse prices
const parsePrice = (price) => {
  if (price === null || price === undefined || isNaN(Number(price))) {
    return 0;
  }
  return Number(price);
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() => {
    try {
      const localData = localStorage.getItem('assistHubCart');
      return localData ? JSON.parse(localData) : [];
    } catch (error) {
      console.error("Failed to parse cart from localStorage", error);
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem('assistHubCart', JSON.stringify(cartItems));
  }, [cartItems]);

  const addToCart = (campaignToAdd) => {
    setCartItems(prevItems => {
      // Check if this is an organization campaign with quantity
      const isOrgCampaign = campaignToAdd.type === 'organization';
      
      if (isOrgCampaign) {
        // For organization campaigns, check if it already exists
        const existingItemIndex = prevItems.findIndex(item => 
          item.id === campaignToAdd.id && item.type === 'organization'
        );
        
        if (existingItemIndex >= 0) {
          // Update quantity if item already exists
          const updatedItems = [...prevItems];
          updatedItems[existingItemIndex] = {
            ...updatedItems[existingItemIndex],
            quantity: campaignToAdd.quantity,
            totalPrice: parsePrice(campaignToAdd.product_price) * campaignToAdd.quantity
          };
          return updatedItems;
        } else {
          // Add new organization campaign with quantity
          return [...prevItems, {
            ...campaignToAdd,
            quantity: campaignToAdd.quantity || 1,
            totalPrice: parsePrice(campaignToAdd.product_price) * (campaignToAdd.quantity || 1)
          }];
        }
      } else {
        // For recipient campaigns, check if it already exists
        const existingItem = prevItems.find(item => 
          item.id === campaignToAdd.id && item.type !== 'organization'
        );
        
        // A recipient campaign can only be in the cart once
        if (existingItem) {
          return prevItems;
        }
        
        // Add new recipient campaign (quantity always 1)
        return [...prevItems, {
          ...campaignToAdd,
          quantity: 1,
          totalPrice: parsePrice(campaignToAdd.product_price)
        }];
      }
    });
  };

  const removeFromCart = (campaignId) => {
    setCartItems(prevItems => prevItems.filter(item => item.id !== campaignId));
  };

  const updateQuantity = (campaignId, newQuantity) => {
    if (newQuantity < 1) return;
    
    setCartItems(prevItems => 
      prevItems.map(item => 
        item.id === campaignId 
          ? {
              ...item,
              quantity: newQuantity,
              totalPrice: parsePrice(item.product_price) * newQuantity
            }
          : item
      )
    );
  };

  const clearCart = () => {
    setCartItems([]);
  };

  // Calculate total cart value
  const cartTotal = cartItems.reduce((total, item) => {
    return total + (item.totalPrice || parsePrice(item.product_price));
  }, 0);

  // Calculate total number of items (considering quantities)
  const itemCount = cartItems.reduce((count, item) => {
    return count + (item.quantity || 1);
  }, 0);

  // Calculate number of distinct items
  const distinctItemCount = cartItems.length;

  const value = {
    cartItems,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    cartTotal,
    itemCount,
    distinctItemCount
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};